import { Phone, Building, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import InlineCallStatus from '@/components/dialer/InlineCallStatus';
import { dialIntent } from '@/services/dialer/dialRules';

/**
 * KanbanView, vue « Bloc » du dialer.
 *
 * #4 (2026-09-01), CETTE VUE N'AFFICHAIT RIEN. Constaté à l'écran : quatre
 * colonnes à 0 alors que les prospects filtrés lui étaient bien passés.
 *
 * Cause : elle classait sur `prospect.status` avec un vocabulaire à elle :
 * `pending` / `calling` / `completed` / `failed`. Or l'ADR-032 (cf.
 * services/dialer/prospectStatus.ts) réduit `ProspectQueue.status` à TROIS
 * valeurs, `active`, `do_not_call`, `placeholder`, et désigne nommément les
 * bonnes sources :
 *
 *   « Quel badge afficher ? »       → dialIntent(prospect, lastCall)
 *   « Cet appel est-il en cours ? » → activeCalls / prospectCallMap
 *
 * Sur les 42 prospects du jeu de test, une seule des quatre valeurs attendues
 * existait encore (`pending`, alias déprécié) ; `calling`, `completed` et
 * `failed` n'apparaissaient nulle part. D'où les colonnes vides.
 *
 * Troisième occurrence du même défaut après #72 et #1 : un vocabulaire de
 * statuts inventé dans un composant, que rien ne sert.
 *
 * ─── Le drag & drop a été retiré, délibérément ───────────────────────────
 * Il appelait `onStatusChange(prospect, 'completed' | 'failed' | …)`, qui
 * écrivait `ProspectQueue.status`. Deux fautes en une : la valeur écrite est
 * hors du vocabulaire ADR-032, et l'ADR interdit précisément cette écriture :
 * `handleManualStatusChange` (ContactsScreen.tsx:1129) avait déjà été corrigé
 * pour cette raison, le kanban était resté en arrière.
 *
 * Le classement étant désormais DÉRIVÉ du dernier outcome, déplacer une carte
 * à la main n'aurait de toute façon rien changé à l'écran : la carte serait
 * revenue à sa place au premier rendu. Une poignée qui ne déplace rien est
 * pire qu'une absence de poignée.
 */

interface ProspectShape {
  id: string;
  prospect_name?: string | null;
  prospect_company?: string | null;
  prospect_phone?: string | null;
  status?: string | null;
}

type CallShape = Parameters<typeof InlineCallStatus>[0]['call'];

interface KanbanViewProps {
  prospects: ProspectShape[];
  onProspectClick: (prospect: ProspectShape) => void;
  callMap?: Record<string, NonNullable<CallShape>>;
  /** Dernier appel par numéro, nécessaire à dialIntent. */
  latestCallByPhone?: Record<string, unknown>;
  currentTime?: Date | number;
}

type ColonneId = 'attente' | 'encours' | 'termine' | 'echoue';

// `title` is resolved from the catalog at render time (see `columns` in
// the component below), reusing generic state words: this is a derived
// call-progress grouping local to this view, never a stored status or a
// call outcome, so it doesn't belong in callOutcomeLabels.
const COLUMN_META: { id: ColonneId; titleKey: string; bgColor: string }[] = [
  { id: 'attente', titleKey: 'dialer:tasks.pendingTasks', bgColor: 'surface-1' },
  { id: 'encours', titleKey: 'common:states.running', bgColor: 'bg-amber-50/60' },
  { id: 'termine', titleKey: 'common:states.completed', bgColor: 'bg-green-50/60' },
  { id: 'echoue', titleKey: 'common:states.failed', bgColor: 'bg-red-50/40' },
];

/**
 * Colonne d'un prospect, dérivée, jamais stockée.
 *
 *  en cours : un appel actif porte ce numéro (prospectCallMap fait foi)
 *  attente  : jamais appelé            → dialIntent 'fresh'
 *  échoué   : tenté, humain pas joint  → dialIntent 'no_answer'
 *  terminé  : l'appel a conclu, humain joint, RDV pris, pas intéressé,
 *             DNC ou rappel convenu    → 'contacted' | 'archived' | 'callback'
 */
function colonneDe(
  prospect: ProspectShape,
  lastCall: unknown,
  callMap: Record<string, NonNullable<CallShape>>,
): ColonneId {
  const phone = prospect.prospect_phone || '';
  if (phone && callMap[phone]) return 'encours';

  switch (dialIntent(prospect, lastCall)) {
    case 'fresh':     return 'attente';
    case 'no_answer': return 'echoue';
    default:          return 'termine';
  }
}

export default function KanbanView({
  prospects,
  onProspectClick,
  callMap = {},
  latestCallByPhone = {},
  currentTime,
}: KanbanViewProps) {
  const { t } = useTranslation(['dialer', 'common']);
  const columns = COLUMN_META.map((c) => ({ ...c, title: t(c.titleKey) }));
  const parColonne: Record<ColonneId, ProspectShape[]> =
    { attente: [], encours: [], termine: [], echoue: [] };
  for (const p of prospects) {
    const lastCall = latestCallByPhone[p.prospect_phone || ''] || null;
    parColonne[colonneDe(p, lastCall, callMap)].push(p);
  }

  return (
    <div className="grid grid-cols-4 gap-4">
      {columns.map((column) => {
        const columnProspects = parColonne[column.id];

        return (
          <div key={column.id} className={`flex flex-col rounded-2xl ${column.bgColor} border border-border/50`}>
            <div className="p-4 border-b border-border/50">
              <h3 className="font-medium text-foreground/80 text-sm">{column.title}</h3>
              <p className="text-2xl font-semibold text-foreground mt-1">{columnProspects.length}</p>
            </div>

            <div
              data-testid={`kanban-col-${column.id}`}
              className="p-3 space-y-2 min-h-[200px] overflow-y-auto"
              style={{ maxHeight: 'calc(100vh - 300px)' }}
            >
              {columnProspects.length === 0 && (
                <p className="text-xs text-muted-foreground/70 px-1 py-2">{t('dialer:kanbanView.noContacts')}</p>
              )}

              {columnProspects.map((prospect: ProspectShape) => (
                <div
                  key={prospect.id}
                  data-testid="kanban-carte"
                  onClick={() => onProspectClick(prospect)}
                  className="bg-card/80 rounded-xl p-3 border border-border/50 cursor-pointer hover:bg-[var(--vf-accent-bg)] hover:border-[var(--vf-accent-bd)] hover:shadow-sm transition-all"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <User className="w-3.5 h-3.5 text-muted-foreground/60 flex-shrink-0" />
                    <p className="font-medium text-sm text-foreground truncate">{prospect.prospect_name}</p>
                  </div>

                  {prospect.prospect_company && (
                    <div className="flex items-center gap-2 mb-1.5">
                      <Building className="w-3 h-3 text-muted-foreground/60 flex-shrink-0" />
                      <p className="text-xs text-muted-foreground truncate">{prospect.prospect_company}</p>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <Phone className="w-3 h-3 text-muted-foreground/60 flex-shrink-0" />
                    <p className="text-xs text-muted-foreground font-mono">{prospect.prospect_phone}</p>
                  </div>

                  {prospect.prospect_phone && callMap[prospect.prospect_phone] && (
                    <div className="mt-2">
                      <InlineCallStatus
                        call={callMap[prospect.prospect_phone] ?? null}
                        currentTime={currentTime}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
